import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { motion, AnimatePresence } from 'motion/react';
import {
  Archive, File, Flag, Inbox, Moon, PenSquare, RefreshCw, Search, Send, Settings, ShieldAlert, Sparkles, Sun, Trash2, Users, Workflow, MessageSquareText,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { formatMailbox } from '@dts/core';
import { useUi } from '@/lib/store';
import { useContacts, useFolders } from '@/lib/queries';
import { composeNew, composeWithAi } from '@/lib/compose';
import { useCrm } from '@/lib/crm';
import { Avatar } from './Avatar';
import { Kbd } from './ui';

export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  const setOpen = useUi((s) => s.setPalette);
  const setView = useUi((s) => s.setView);
  const openAsk = useUi((s) => s.openAsk);
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const folders = useFolders().data ?? [];
  const contacts = useContacts().data?.contacts ?? [];
  const qc = useQueryClient();
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-start justify-center pt-[14vh]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -10 }}
            transition={{ type: 'spring', stiffness: 500, damping: 36 }}
            className="glass relative w-[640px] overflow-hidden rounded-2xl bg-raised/95 shadow-[0_40px_120px_-20px_rgba(0,0,0,.6)]"
          >
            <Command loop shouldFilter onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}>
              <div className="flex items-center gap-3 border-b border-line px-4">
                <Search className="size-4 text-muted" />
                <Command.Input autoFocus value={q} onValueChange={setQ} placeholder="Search actions, folders, people — or ask AI anything…" className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-faint" />
                <Kbd>Esc</Kbd>
              </div>
              <Command.List className="max-h-[420px] overflow-y-auto p-2">
                <Command.Empty className="px-3 py-6 text-center text-[13px] text-muted">No matches — press the AI items below.</Command.Empty>
                {q.trim().length > 2 && (
                  <Command.Group heading="AI" forceMount>
                    <Item forceMount value={`ask ${q}`} icon={<Sparkles className="text-accent" />} onSelect={run(() => openAsk(q))}>
                      Ask your inbox: <span className="ai-text font-semibold">“{q}”</span>
                    </Item>
                    <Item forceMount value={`write ${q}`} icon={<PenSquare className="text-accent" />} onSelect={run(() => composeWithAi(q))}>
                      Write an email: <span className="font-medium">“{q}”</span>
                    </Item>
                    <Item forceMount value={`search ${q}`} icon={<Search />} onSelect={run(() => setView({ kind: 'mail', search: q }))}>
                      Search mail for “{q}”
                    </Item>
                  </Command.Group>
                )}
                <Command.Group heading="Actions">
                  <Item icon={<PenSquare />} shortcut="C" onSelect={run(() => composeNew())}>New message</Item>
                  <Item icon={<Sparkles />} onSelect={run(() => composeWithAi())}>Compose with AI</Item>
                  <Item icon={<MessageSquareText />} shortcut="Ctrl J" onSelect={run(() => openAsk())}>Ask AI about your mail</Item>
                  <Item icon={<RefreshCw />} onSelect={run(() => qc.invalidateQueries())}>Refresh everything</Item>
                  <Item icon={local.theme === 'dark' ? <Sun /> : <Moon />} onSelect={run(() => setLocal({ theme: local.theme === 'dark' ? 'light' : 'dark' }))}>
                    Switch to {local.theme === 'dark' ? 'light' : 'dark'} theme
                  </Item>
                  <Item icon={<Workflow />} onSelect={run(() => useCrm.getState().setPanel(true))}>CRM activity</Item>
                </Command.Group>
                <Command.Group heading="Go to">
                  <Item icon={<Sun />} onSelect={run(() => setView({ kind: 'today' }))}>Today</Item>
                  <Item icon={<Inbox />} onSelect={run(() => setView({ kind: 'mail', folder: 'Inbox' }))}>Inbox</Item>
                  <Item icon={<Sparkles />} onSelect={run(() => setView({ kind: 'mail', folder: 'Inbox', filter: 'needs_reply' }))}>Needs reply</Item>
                  <Item icon={<Flag />} onSelect={run(() => setView({ kind: 'mail', filter: 'flagged' }))}>Flagged</Item>
                  <Item icon={<Send />} onSelect={run(() => setView({ kind: 'mail', folder: 'Sent' }))}>Sent</Item>
                  <Item icon={<File />} onSelect={run(() => setView({ kind: 'mail', folder: 'Drafts' }))}>Drafts</Item>
                  <Item icon={<Archive />} onSelect={run(() => setView({ kind: 'mail', folder: 'Archive' }))}>Archive</Item>
                  <Item icon={<ShieldAlert />} onSelect={run(() => setView({ kind: 'mail', folder: 'Junk' }))}>Junk</Item>
                  <Item icon={<Trash2 />} onSelect={run(() => setView({ kind: 'mail', folder: 'Trash' }))}>Trash</Item>
                  {folders.filter((f) => !f.isSystem).map((f) => (
                    <Item key={f.name} icon={<Inbox />} onSelect={run(() => setView({ kind: 'mail', folder: f.name }))}>{f.name}</Item>
                  ))}
                  <Item icon={<Users />} onSelect={run(() => setView({ kind: 'contacts' }))}>Contacts</Item>
                  <Item icon={<Settings />} onSelect={run(() => setView({ kind: 'settings' }))}>Settings</Item>
                </Command.Group>
                {contacts.length > 0 && (
                  <Command.Group heading="People">
                    {contacts.slice(0, 300).map((c) => (
                      <Item
                        key={c.id}
                        value={`${c.name} ${c.emails.join(' ')} ${c.company ?? ''}`}
                        icon={<Avatar name={c.name} address={c.emails[0] ?? c.name} size={22} />}
                        onSelect={run(() => composeNew({ to: c.emails[0] ? [formatMailbox({ name: c.name, address: c.emails[0] })] : [] }))}
                      >
                        <span className="font-medium">{c.name}</span>
                        <span className="ml-2 text-faint">{c.emails[0]}</span>
                      </Item>
                    ))}
                  </Command.Group>
                )}
              </Command.List>
            </Command>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Item({ icon, children, onSelect, shortcut, value, forceMount }: { icon: React.ReactNode; children: React.ReactNode; onSelect: () => void; shortcut?: string; value?: string; forceMount?: boolean }) {
  return (
    <Command.Item value={value} forceMount={forceMount} onSelect={onSelect} className="flex h-10 cursor-default items-center gap-3 rounded-xl px-3 text-[13.5px] text-fg [&_svg]:size-4 [&_svg]:text-muted">
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </Command.Item>
  );
}
